import { authFetch } from "@/lib/api-client";
import type {
  AdminVendorRow,
  AdminVendorWritePayload,
  VendorContactEmailRow,
  PendingVendorOnboardingRow,
  PendingOrderHandoffRow,
} from "@/features/admin/marketplace/types/admin-marketplace.types";

// ─── Reads ─────────────────────────────────────────────────────────────────

export async function fetchAdminVendors(): Promise<AdminVendorRow[]> {
  const { data } = await authFetch<AdminVendorRow[]>(
    "/marketplace/admin/vendors",
  );
  return data;
}

export async function fetchAdminVendor(
  vendorId: string,
): Promise<AdminVendorRow> {
  const { data } = await authFetch<AdminVendorRow>(
    `/marketplace/admin/vendors/${encodeURIComponent(vendorId)}`,
  );
  return data;
}

// ─── Mutations ─────────────────────────────────────────────────────────────

export async function createAdminVendor(
  payload: AdminVendorWritePayload,
): Promise<AdminVendorRow> {
  const { data } = await authFetch<AdminVendorRow>(
    "/marketplace/admin/vendors",
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );
  return data;
}

export async function replaceAdminVendor(
  vendorId: string,
  payload: AdminVendorWritePayload,
): Promise<AdminVendorRow> {
  const { data } = await authFetch<AdminVendorRow>(
    `/marketplace/admin/vendors/${encodeURIComponent(vendorId)}`,
    {
      method: "PUT",
      body: JSON.stringify(payload),
    },
  );
  return data;
}

export async function activateAdminVendor(
  vendorId: string,
): Promise<AdminVendorRow> {
  const { data } = await authFetch<AdminVendorRow>(
    `/marketplace/admin/vendors/${encodeURIComponent(vendorId)}/activate`,
    { method: "POST" },
  );
  return data;
}

export async function deactivateAdminVendor(
  vendorId: string,
): Promise<AdminVendorRow> {
  const { data } = await authFetch<AdminVendorRow>(
    `/marketplace/admin/vendors/${encodeURIComponent(vendorId)}/deactivate`,
    { method: "POST" },
  );
  return data;
}

// Approving makes an address a live egress target, so the API gates these on
// super-tenant on top of the permission key.

export async function approveVendorContactEmail(
  vendorId: string,
  emailId: string,
): Promise<VendorContactEmailRow> {
  const { data } = await authFetch<VendorContactEmailRow>(
    `/marketplace/admin/vendors/${encodeURIComponent(vendorId)}/contact-emails/${encodeURIComponent(emailId)}/approve`,
    { method: "POST" },
  );
  return data;
}

export async function revokeVendorContactEmail(
  vendorId: string,
  emailId: string,
): Promise<VendorContactEmailRow> {
  const { data } = await authFetch<VendorContactEmailRow>(
    `/marketplace/admin/vendors/${encodeURIComponent(vendorId)}/contact-emails/${encodeURIComponent(emailId)}/revoke`,
    { method: "POST" },
  );
  return data;
}

export async function fetchPendingVendors(): Promise<PendingVendorOnboardingRow[]> {
  const { data } = await authFetch<PendingVendorOnboardingRow[]>(
    "/marketplace/admin/vendors/pending",
  );
  return data;
}

export async function linkPendingVendor(
  pendingId: string,
  vendorId: string,
  tenantId?: string,
): Promise<PendingVendorOnboardingRow> {
  const { data } = await authFetch<PendingVendorOnboardingRow>(
    `/marketplace/admin/vendors/pending/${encodeURIComponent(pendingId)}/link`,
    {
      method: "POST",
      body: JSON.stringify({
        vendorId,
        ...(tenantId ? { tenantId } : {}),
      }),
    },
  );
  return data;
}

export async function rejectPendingVendor(
  pendingId: string,
): Promise<PendingVendorOnboardingRow> {
  const { data } = await authFetch<PendingVendorOnboardingRow>(
    `/marketplace/admin/vendors/pending/${encodeURIComponent(pendingId)}/reject`,
    { method: "POST" },
  );
  return data;
}

export async function fetchPendingOrderHandoffs(): Promise<PendingOrderHandoffRow[]> {
  const { data } = await authFetch<PendingOrderHandoffRow[]>(
    "/marketplace/admin/orders/pending-handoffs",
  );
  return data;
}

export async function retryAdminOrderHandoff(
  orderId: string,
): Promise<{ orderId: string; status: string }> {
  const { data } = await authFetch<{ orderId: string; status: string }>(
    `/marketplace/admin/orders/${encodeURIComponent(orderId)}/retry-handoff`,
    { method: "POST" },
  );
  return data;
}
